import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import PaginationButton from "@/components/ui/pagination-button";
import { Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { FileText } from "lucide-react";
import ReceivedBadge from "@/routes/(inventory)/-module-components/badges/received";
import OrderedBadge from "@/routes/(inventory)/-module-components/badges/ordered";
import CancelledBadge from "@/routes/(inventory)/-module-components/badges/cancelled";
import InProgressBadge from "@/routes/(inventory)/-module-components/badges/in-progress";
import Details from "./details-dialog";
import RejectedBadge from "@/routes/(inventory)/-module-components/badges/rejected";
import {Route} from "@/routes/(pharma)/_layout/inventory/transfer-history/index"
import { useRouter } from "@tanstack/react-router";
import dayjs from "dayjs";
import { TransferHistoryStatus } from "@/gen/inventory/common_pb";
import PendingBadge from "@/routes/(inventory)/-module-components/badges/pending";
import { useNavigate } from "@tanstack/react-router";
import { PharmacySize } from "@/gen/auth/common_pb";
import TransferredBadge from "@/routes/(inventory)/-module-components/badges/transferred";

const filterOptions = [
  { value: "all", label: "All", badge: <FileText className="w-3 h-3" /> },
  { value: "pending_approval", label: "Pending Approval", badge: <PendingBadge /> },
  { value: "completed", label: "Transferred", badge: <OrderedBadge /> },
  { value: "cancelled", label: "Cancelled", badge: <CancelledBadge /> },
  { value: "rejected", label: "Not Received", badge: <RejectedBadge /> },
  { value: "approved", label: "In Progress", badge: <InProgressBadge /> },
];

const Component = () => {

  const data = Route.useLoaderData();
  const search = Route.useSearch();
  const router = useRouter();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange | undefined>();

  // Styled wrappers
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={`${tableCellClass} font-medium ${className}`} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={`${tableHeadClass} ${className}`} {...props} />
  );

  const pharmacySize = localStorage.getItem('pharmacySize')

  return (
    <div className="overflow-y-scroll h-screen pb-48">
      <div className="flex items-center justify-between mb-2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink
              onClick={() => {
                navigate({
                  to: "/inventory",
                });
              }}
              style={{ cursor: "pointer"}}
            >
              Inventory</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Transfer History</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2">
          <Select value={search.status} onValueChange={(v) => {
            router.navigate({
              search: {
                status: v
              }
            })
          }}>
            <SelectTrigger className="w-50">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              {filterOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value} className="flex flex-row items-center gap-2">
                  {opt.badge && <span className="inline-flex items-center gap-2">{opt.badge} {opt.label}</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DateRangePicker className="" date={dateRange} onDateChange={setDateRange} />
        </div>
      </div>
      <hr className="my-4" />
      <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        <Table className="text-sm">
          <TableHeader>
            <TableRow>
              <StyledTableHead>Date</StyledTableHead>
              <StyledTableHead>Source</StyledTableHead>
              <StyledTableHead>Destination</StyledTableHead>
              <StyledTableHead>Items</StyledTableHead>
              <StyledTableHead>Status</StyledTableHead>
              <StyledTableHead>Initiated By</StyledTableHead>
              <StyledTableHead>Actions</StyledTableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.transferHistory.map((transfer, index) => (
              <TableRow key={index}>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>{dayjs(transfer.createdAt?.toDate()).format("DD/MM/YYYY")}</StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>{pharmacySize === PharmacySize.SIZE_SMALL.toString() ? transfer.sourceBranchName : transfer.sourceStockPointName}</StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>{pharmacySize === PharmacySize.SIZE_SMALL.toString() ? transfer.destinationBranchName : transfer.destinationStockPointName}</StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>{transfer.items.length}</StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>
                  {(() => {
                    switch (transfer.status) {
                      case TransferHistoryStatus.PENDING_APPROVAL:
                        return <PendingBadge />;
                      case TransferHistoryStatus.COMPLETED:
                        return <TransferredBadge />;
                      case TransferHistoryStatus.CANCELLED:
                        return <CancelledBadge />;
                      case TransferHistoryStatus.REJECTED:
                        return <RejectedBadge />;
                      case TransferHistoryStatus.APPROVED:
                        return <InProgressBadge />;
                      default:
                        return <ReceivedBadge />;
                    }
                  })()}
                </StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>{transfer.requestedBy?.fullName}</StyledTableCell>
                <StyledTableCell className={index === data.totalCount ? tableCellLastRowClass : ""}>
                  <Details 
                    data={transfer.items} 
                    currentStatus={transfer.status} 
                    transferId={transfer.transferRequestId} 
                    source={{
                    stockPointId: transfer.sourceStockPointId,
                    pharmacyCode: transfer.sourceBranchCode
                    }} 
                    destination={{
                    stockPointId: transfer.destinationStockPointId,
                    pharmacyCode: transfer.destinationBranchCode
                  }}/>
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
          <p>{data.totalCount} <span className="text-gray-500 font-light">count</span></p>
          <div className="flex items-center gap-1">
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  pageNumber: 1,
                  pageSize: search.pageSize || 10, 
                  status: search.status || 'all'
                }
              });
            }} disabled={search.pageNumber === 1}>
              &laquo;
            </PaginationButton>
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  pageNumber: search.pageNumber - 1,
                  pageSize: search.pageSize || 10, 
                  status: search.status || 'all'
                }
              });
            }} disabled={search.pageNumber === 1}>
              &lsaquo;
            </PaginationButton>
            {[...Array(
              Math.ceil(data.totalCount / (search.pageSize || 10))
            )].map((_, idx) => (
              <PaginationButton
                key={idx + 1}
                active={
                  search.pageNumber === idx + 1
                }
                onClick={() => {
                  router.navigate({
                    search: {
                      pageNumber: idx + 1,
                      pageSize: search.pageSize || 10, 
                      status: search.status || 'all'
                    }
                  });
                }}
              >
                {idx + 1}
              </PaginationButton>
            ))}
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  pageNumber: search.pageNumber + 1,
                  pageSize: search.pageSize || 10, 
                  status: search.status || 'all'
                }
              });
            }} disabled={
              search.pageNumber === Math.ceil(data.totalCount / (search.pageSize || 10))
            }>
              &rsaquo;
            </PaginationButton>
            <PaginationButton onClick={() => {
              router.navigate({
                search: {
                  pageNumber: Math.ceil(data.totalCount / (search.pageSize || 10)),
                  pageSize: search.pageSize || 10, 
                  status: search.status || 'all'
                }
              });
            }} disabled={
              search.pageNumber === Math.ceil(data.totalCount / (search.pageSize || 10))
            }>
              &raquo;
            </PaginationButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Component;
